import React, { HTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import chroma from 'chroma-js';

import { usePanelTabContext } from './PanelTabs';
import { semantic_colors, text_styles } from '../../../foundation';
import { Divider } from '../../divider';
import { Stack } from '../../stack';
import { BaseText } from '../../text';
import { IconCircleQuestion } from '../../icons/generated';

const palette = {
  base: {
    text: semantic_colors.content.secondary,
    caption: semantic_colors.content.tertiary,
    divider: semantic_colors.border.primary,
    icon: semantic_colors.content.tertiary,
  },
  selected: {
    text: semantic_colors.accent.primary,
    caption: semantic_colors.accent.primary,
    divider: chroma(semantic_colors.accent.primary).alpha(0.3).hex(),
    icon: semantic_colors.content.tertiary,
  },
  base_disabled: {
    text: semantic_colors.content.disabled,
    caption: semantic_colors.content.disabled,
    divider: semantic_colors.border.disabled,
    icon: semantic_colors.content.disabled,
  },
  selected_disabled: {
    text: semantic_colors.accent.disabled,
    caption: semantic_colors.accent.disabled,
    divider: semantic_colors.accent.disabled,
    icon: semantic_colors.content.disabled,
  },
};

export interface PanelTabProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onClick'> {
  id: string;
  onClick?: (id: string) => void;
  title: string;
  number: number;
  suffix: string;
  helperIcon?: (icon: ReactNode) => JSX.Element;
  highlight?: boolean;
  caption?: string;
  disabled?: boolean;
  /** @default 'ko-KR' */
  locale?: 'ko-KR' | 'en-US';
}

export const PanelTab = ({
  id,
  onClick,
  className,
  caption,
  highlight,
  helperIcon,
  suffix,
  title,
  number,
  disabled,
  locale = 'ko-KR',
  ...props
}: PanelTabProps) => {
  const { activeTabId, onChange } = usePanelTabContext();
  const selected = activeTabId === id;
  const color = disabled
    ? selected
      ? palette.selected_disabled
      : palette.base_disabled
    : selected
    ? palette.selected
    : palette.base;

  const formatted =
    number >= Math.pow(10, 7)
      ? Intl.NumberFormat(locale, {
          notation: 'compact',
          maximumFractionDigits: 1,
          useGrouping: true,
        }).format(number)
      : number.toLocaleString();

  const handleClick = (id: string) => {
    onClick?.(id);
    onChange?.(id);
  };

  return (
    <TabItem
      type='button'
      selected={selected}
      disabled={disabled}
      className={classNames('pds-panel-tab', className)}
      role='tab'
      aria-selected={selected}
      aria-disabled={disabled}
      tabIndex={0}
      onClick={() => handleClick(id)}
      id={id}
      {...props}
    >
      {highlight && (
        <Highlight data-testid='highlight'>
          <HighlightFlag disabled={Boolean(disabled)} />
        </Highlight>
      )}
      <Stack direction='column' style={{ maxWidth: '100%' }}>
        <Stack direction='row' align='center' gap={4}>
          <BaseText kind='Caption_11_Regular' color='inherit'>
            {title}
          </BaseText>
          {helperIcon &&
            helperIcon(
              <div style={{ lineHeight: 0 }}>
                <IconCircleQuestion width={12} height={12} color={semantic_colors.content.tertiary} />
              </div>,
            )}
        </Stack>
        <Count kind='Heading_20_Bold' color='inherit'>
          <StyledNumber>{formatted}</StyledNumber>
          <StyledSuffix>{suffix}</StyledSuffix>
        </Count>
      </Stack>
      {!!caption && (
        <>
          <Divider color={color.divider} />
          <StyledCaption kind='Caption_11_Regular' color={color.caption}>
            {caption}
          </StyledCaption>
        </>
      )}
    </TabItem>
  );
};

const StyledCaption = styled(BaseText)`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TabItem = styled.button<{ selected: boolean }>`
  position: relative;
  cursor: pointer;
  display: flex;
  min-width: 120px;
  padding: 5px 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  border-radius: 8px;
  color: ${({ selected }) => (selected ? palette.selected.text : palette.base.text)};
  background-color: ${({ selected }) => (selected ? semantic_colors.accent.light : semantic_colors.background.surface)};
  border: 1px solid ${({ selected }) => (selected ? semantic_colors.accent.primary : semantic_colors.border.secondary)};
  ${text_styles.Caption_11_Regular};
  white-space: nowrap;
  transition: all 0.1s linear;

  ${({ selected }) =>
    !selected &&
    `
    &:hover:not(:disabled) {
      color: ${semantic_colors.accent.primary};
      border-color: ${semantic_colors.accent.primary};
      ${Divider} {
        background-color: ${chroma(semantic_colors.accent.primary).alpha(0.3).hex()};
      }
      ${StyledCaption} {
        color: ${semantic_colors.accent.primary};
      }
    }
  `}
  &:focus {
    outline: 3px solid ${semantic_colors.tab_focus};
    outline-offset: -3px;
    box-shadow: none;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &:active {
    background-color: ${chroma(semantic_colors.accent.primary).alpha(0.1).hex()};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${({ selected }) => (selected ? palette.selected_disabled.text : palette.base_disabled.text)};
    background-color: ${({ selected }) =>
      selected ? semantic_colors.accent.light : semantic_colors.background.disabled};
    border-color: ${({ selected }) => (selected ? semantic_colors.accent.disabled : semantic_colors.border.disabled)};
  }
`;

const Highlight = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  border-radius: 0 7px 0 0;
  overflow: hidden;
`;

const HighlightFlag = ({ disabled }: { disabled: boolean }) => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M14 0L0 0L14 14L14 0Z'
      fill={disabled ? semantic_colors.state.negative_disabled : semantic_colors.state.negative}
    />
  </svg>
);

const Count = styled(BaseText)`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;

const StyledNumber = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StyledSuffix = styled.span`
  margin-left: 2px;
  margin-bottom: 2px;
  ${text_styles.Body_12_SemiBold};
`;
